import "./styles.css";
import { asset, setBasePath, consumeSpaRedirect } from "./lib/base.js";
import { state } from "./lib/state.js";
import { currentRoute, navigate } from "./lib/router.js";
import { cartCount } from "./lib/cart.js";
import { escapeHtml } from "./lib/util.js";
import { bindHome, renderHomeResults, setupInstallHint } from "./views/home.js";
import { bindItem, renderItem } from "./views/item.js";
import { bindFarm, renderFarm } from "./views/farm.js";
import { bindBosses, renderBossList, renderBossDetail } from "./views/bosses.js";
import { bindCart, renderCart } from "./views/cart-view.js";
import { registerServiceWorker } from "./sw-register.js";

const els = {
  brand: document.getElementById("brand"),
  backBtn: document.getElementById("backBtn"),
  cartBtn: document.getElementById("cartBtn"),
  cartCount: document.getElementById("cartCount"),
  tabs: document.getElementById("tabs"),
  home: document.getElementById("view-home"),
  itemView: document.getElementById("view-item"),
  farmView: document.getElementById("view-farm"),
  bossesView: document.getElementById("view-bosses"),
  bossView: document.getElementById("view-boss"),
  cartView: document.getElementById("view-cart"),
  itemPanel: document.getElementById("itemPanel"),
  farmPanel: document.getElementById("farmPanel"),
  bossesPanel: document.getElementById("bossesPanel"),
  bossPanel: document.getElementById("bossPanel"),
  cartPanel: document.getElementById("cartPanel"),
  searchForm: document.getElementById("searchForm"),
  searchInput: document.getElementById("searchInput"),
  results: document.getElementById("results"),
  heroTagline: document.getElementById("heroTagline"),
  status: document.getElementById("status"),
  credit: document.getElementById("credit"),
  tipLink: document.getElementById("tipLink"),
  installHint: document.getElementById("installHint"),
  installText: document.getElementById("installText"),
  installBtn: document.getElementById("installBtn"),
};

function siteName() {
  return state.config?.siteName || "Core Keeper Craft";
}

function setStatus(message, isError = false) {
  els.status.textContent = message || "";
  els.status.classList.toggle("error", Boolean(isError));
}

function updateCartBadge() {
  const n = cartCount();
  if (n > 0) {
    els.cartCount.hidden = false;
    els.cartCount.textContent = String(n);
  } else {
    els.cartCount.hidden = true;
  }
}

function setSeo({ title, description, path }) {
  document.title = title;
  const desc = document.querySelector('meta[name="description"]');
  if (desc) desc.setAttribute("content", description);
  const canonical = document.querySelector('link[rel="canonical"]');
  const siteUrl = (state.config?.siteUrl || "").replace(/\/$/, "");
  const href = `${siteUrl}/${(path || "").replace(/^\//, "")}`;
  if (canonical) canonical.setAttribute("href", href);
  else {
    const link = document.createElement("link");
    link.rel = "canonical";
    link.href = href;
    document.head.appendChild(link);
  }
}

function hideAllViews() {
  for (const v of [
    els.home,
    els.itemView,
    els.farmView,
    els.bossesView,
    els.bossView,
    els.cartView,
  ]) {
    v.hidden = true;
  }
}

function setTab(routeName) {
  const tabRoute =
    routeName === "item" ? "home" : routeName === "boss" ? "bosses" : routeName;
  for (const btn of els.tabs.querySelectorAll(".tab")) {
    const on = btn.dataset.route === tabRoute;
    btn.setAttribute("aria-current", on ? "page" : "false");
  }
}

function showBack(show) {
  els.backBtn.hidden = !show;
}

async function waitReady() {
  if (state.ready) return;
  await new Promise((resolve) => {
    const t = setInterval(() => {
      if (state.ready) {
        clearInterval(t);
        resolve();
      }
    }, 40);
  });
}

async function route() {
  const r = currentRoute();
  hideAllViews();
  setTab(r.name);
  els.brand.textContent = siteName();

  if (r.name === "item") {
    els.itemView.hidden = false;
    showBack(true);
    els.itemPanel.innerHTML = `<p class="empty">Loading…</p>`;
    await waitReady();
    const detail = renderItem(els, r.slug);
    setSeo({
      title: detail ? `${detail.name} — ${siteName()}` : `Not found — ${siteName()}`,
      description: detail?.description || "Item detail",
      path: `item/${encodeURIComponent(r.slug)}/`,
    });
    return;
  }

  if (r.name === "farm") {
    els.farmView.hidden = false;
    showBack(false);
    await waitReady();
    renderFarm(els);
    setSeo({
      title: `Farm planner — ${siteName()}`,
      description: "Core Keeper crop grow times and seed cart queue.",
      path: "farm/",
    });
    return;
  }

  if (r.name === "bosses") {
    els.bossesView.hidden = false;
    showBack(false);
    await waitReady();
    renderBossList(els);
    setSeo({
      title: `Boss prep — ${siteName()}`,
      description: "Core Keeper boss gear, food, and torch checklists.",
      path: "bosses/",
    });
    return;
  }

  if (r.name === "boss") {
    els.bossView.hidden = false;
    showBack(true);
    await waitReady();
    const boss = renderBossDetail(els, r.slug);
    setSeo({
      title: boss ? `${boss.name} — ${siteName()}` : `Not found — ${siteName()}`,
      description: boss?.summary || "Boss prep checklist",
      path: `boss/${encodeURIComponent(r.slug)}/`,
    });
    return;
  }

  if (r.name === "cart") {
    els.cartView.hidden = false;
    showBack(false);
    await waitReady();
    renderCart(els);
    setSeo({
      title: `Craft cart — ${siteName()}`,
      description: "Craft targets and raw material expansion.",
      path: "cart/",
    });
    return;
  }

  els.home.hidden = false;
  showBack(false);
  renderHomeResults(els);
  setSeo({
    title: `${siteName()} — Core Keeper recipes & prep`,
    description:
      "Search Core Keeper recipes, plan farms, and build boss prep carts. Free Home Screen web app.",
    path: "",
  });
  els.searchInput.focus({ preventScroll: true });
}

function setupConfigHooks(config) {
  if (config.tipUrl && els.tipLink) {
    els.tipLink.hidden = false;
    els.tipLink.href = config.tipUrl;
    els.tipLink.textContent = config.tipLabel || "Support the team";
  }

  // Analytics / ads hooks only — empty at launch (no tokens until core loop works)
  if (config.goatCounter) {
    const script = document.createElement("script");
    script.async = true;
    script.dataset.goatcounter = `https://${config.goatCounter}.goatcounter.com/count`;
    script.src = "https://gc.zgo.at/count.js";
    document.head.appendChild(script);
  }
  if (config.cloudflareAnalytics) {
    const script = document.createElement("script");
    script.defer = true;
    script.src = "https://static.cloudflareinsights.com/beacon.min.js";
    script.setAttribute(
      "data-cf-beacon",
      JSON.stringify({ token: config.cloudflareAnalytics })
    );
    document.body.appendChild(script);
  }
  if (config.adsenseClient && config.adsenseSlot) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(config.adsenseClient)}`;
    script.crossOrigin = "anonymous";
    document.head.appendChild(script);
  }
}

async function loadData() {
  setStatus("Loading catalogue…");
  const config = await fetch(asset("config.json"))
    .then((r) => {
      if (!r.ok) throw new Error("config missing");
      return r.json();
    })
    .catch(() => ({
      siteName: "Core Keeper Craft",
      shortName: "CK Craft",
      siteUrl: "",
      basePath: "/",
      tipUrl: "",
      goatCounter: "",
      cloudflareAnalytics: "",
      adsenseClient: "",
      adsenseSlot: "",
    }));

  state.config = config;
  setBasePath(config.basePath || "/");
  consumeSpaRedirect();
  setupConfigHooks(config);
  setupInstallHint(els);
  updateCartBadge();

  const [meta, items, details, crops, bosses] = await Promise.all([
    fetch(asset("data/meta.json")).then((r) => {
      if (!r.ok) throw new Error("meta missing — run npm run sync");
      return r.json();
    }),
    fetch(asset("data/items.json")).then((r) => {
      if (!r.ok) throw new Error("items missing — run npm run sync");
      return r.json();
    }),
    fetch(asset("data/details.json")).then((r) => {
      if (!r.ok) throw new Error("details missing — run npm run sync");
      return r.json();
    }),
    fetch(asset("data/crops.json")).then((r) => (r.ok ? r.json() : [])),
    fetch(asset("data/bosses.json")).then((r) => (r.ok ? r.json() : [])),
  ]);

  state.meta = meta;
  state.items = items;
  state.details = details;
  state.crops = crops;
  state.bosses = bosses;
  state.ready = true;

  const synced = meta.syncedAt
    ? new Date(meta.syncedAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "unknown";

  els.credit.hidden = false;
  const sourceHint =
    meta.dataMode === "wiki"
      ? `wiki sync`
      : meta.dataMode === "sample"
        ? "sample data"
        : meta.dataMode || "data";
  const sourceLink = meta.source
    ? ` from <a href="${escapeHtml(meta.source)}" target="_blank" rel="noopener">Core Keeper Wiki</a>`
    : "";
  els.credit.innerHTML = `${escapeHtml(items.length.toLocaleString())} items · ${escapeHtml(
    sourceHint
  )}${sourceLink} · synced ${escapeHtml(
    synced
  )}. Fan-made; not affiliated with Pugstorm or Fireshine Games. Data ${
    meta.dataMode === "wiki" ? "CC-BY-SA" : "curated"
  }.`;

  setStatus("");
  await route();
}

bindHome(els);
bindItem(els);
bindFarm(els);
bindBosses(els);
bindCart(els);

els.tabs.addEventListener("click", (e) => {
  const tab = e.target.closest("[data-route]");
  if (!tab) return;
  navigate(tab.dataset.route);
});

els.cartBtn.addEventListener("click", () => navigate("cart"));
els.brand.addEventListener("click", () => navigate("home"));
els.backBtn.addEventListener("click", () => {
  if (history.length > 1) history.back();
  else navigate("home");
});

window.addEventListener("popstate", () => {
  route();
});
window.addEventListener("ck-cart-change", updateCartBadge);

loadData()
  .then(() => registerServiceWorker())
  .catch((err) => {
    console.error(err);
    setStatus(err.message || "Failed to load data", true);
  });
